import { FC } from "react";
import { Button, Grid, Modal, Typography } from "antd";
import { ShopOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "store";
import { snoozeFactorySetup } from "store/slices/layoutSlice";
import SettingFactory from "components/Profiles/SettingFactory";
import useFactorySetupNeeded from "./useFactorySetupNeeded";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

/**
 * dialog ตั้งค่าข้อมูลผู้ประกอบการครั้งแรก
 *
 * ร้านที่อนุมัติแบบ "บัญชี + ร้าน" (ใบสมัครยังไม่มีที่อยู่เข้ารับสินค้า) เข้าระบบมาแล้ว
 * `/auth/profile` คืน contact = null เพราะ vw_factory join ที่อยู่แบบ inner — ชื่อ/โลโก้
 * มุมซ้ายบนจึงว่าง และเขาไม่รู้ว่าต้องไปทำอะไรที่ไหน
 *
 * **กด "ไว้ทีหลัง" ได้** เพื่อไปดูระบบก่อน แต่ยังไม่หายไปไหน:
 *   - เปิดระบบรอบใหม่ → ขึ้นอีกครั้ง (flag อยู่ใน layout ซึ่งไม่ persist)
 *   - ระหว่างที่พักไว้ → ปุ่มมุมซ้ายบน (FactorySetupBrandButton) กดเปิดกลับมาได้ตลอด
 *
 * ฟอร์มข้างในคือตัวเดียวกับหน้า /profiles/setting-factory — ไม่มีเวอร์ชันที่สองให้ลืมแก้
 * บันทึกเสร็จ profile ถูกดึงใหม่ → contact มีค่า → dialog หายเอง
 */
const FactorySetupGate: FC = () => {
  const needsSetup = useFactorySetupNeeded();
  const snoozed = useAppSelector((state) => state.layout.factorySetupSnoozed);
  const dispatch = useAppDispatch();
  /* จอเล็กกว่า sm (<576px) = มือถือ — หัวเรื่องยาวสองบรรทัดกินจอไปครึ่งหนึ่งก่อนถึงช่องกรอก */
  const isMobile = !useBreakpoint().sm;

  if (!needsSetup || snoozed) return null;

  return (
    <Modal
      open
      /*
       * มือถือ: เต็มจอตั้งแต่ขอบบน (centered จะทำให้ `top` ไม่มีผล เพราะ antd ใช้ flex
       * จัดกึ่งกลางแทน) · เดสก์ท็อป: กล่องกลางจอกว้าง 860 ตามเดิม
       */
      centered={!isMobile}
      width={isMobile ? "100%" : 860}
      style={isMobile ? { top: 0, maxWidth: "100vw", margin: 0, paddingBottom: 0 } : undefined}
      footer={null}
      /* ปิดด้วยปุ่ม "ไว้ทีหลัง" เท่านั้น — กากบาท/คลิกฉากหลัง/Esc ไม่ปิด จะได้ไม่ปิดโดยไม่ตั้งใจ */
      closable={false}
      maskClosable={false}
      keyboard={false}
      /*
       * **ห้ามตั้ง zIndex เอง** — antd 5.8 ยังไม่มีการยก z-index ของ popup ที่อยู่ใน Modal
       * ให้อัตโนมัติ (มาใน 5.11) กล่อง Modal เริ่มที่ 1000 ส่วน dropdown ของ Select อยู่ที่
       * 1050 จึงลอยอยู่เหนือพอดี · ตั้งเป็น 1100 เมื่อไหร่ ตัวเลือกจังหวัด/อำเภอ/ตำบล
       * จะไปอยู่ "หลัง" กล่องทันที กดแล้วเหมือนไม่มีข้อมูล (หน้าเต็มไม่เจอเพราะไม่มี Modal)
       * ของสูงสุดในโปรเจกต์นี้คือ 990 ในไฟล์ css — 1000 จึงพอแล้ว
       */
      bodyStyle={{ padding: 0 }}
      /* ฉากหลังมัวแต่ยังเห็นระบบอยู่ — บอกว่ามาถูกที่แล้ว แค่ยังตั้งค่าไม่ครบ */
      maskStyle={{ backdropFilter: "blur(2px)", background: "rgba(15, 23, 42, 0.45)" }}
    >
      {/* แถบหัว — ไล่สีอ่อน ๆ ให้ dialog ไม่ใช่กล่องขาวโล่งที่มีแต่ช่องกรอก */}
      <div
        style={{
          background: "linear-gradient(135deg, #e6f4ff 0%, #f6ffed 100%)",
          borderRadius: isMobile ? 0 : "8px 8px 0 0",
          padding: isMobile ? "12px 16px" : "24px 28px 20px",
          display: "flex",
          gap: isMobile ? 10 : 16,
          alignItems: isMobile ? "center" : "flex-start",
        }}
      >
        <div
          style={{
            flex: "none",
            width: isMobile ? 32 : 44,
            height: isMobile ? 32 : 44,
            borderRadius: isMobile ? 8 : 12,
            background: "#1890ff",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: isMobile ? 16 : 22,
          }}
        >
          <ShopOutlined />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          {/*
            มือถือ: หัวเรื่องสั้น + ไม่มีคำอธิบาย — ของเดิมสองบรรทัดยาวกินจอไปครึ่งหนึ่ง
            ก่อนจะถึงช่องกรอกช่องแรก · สิ่งที่ต้องกรอกมีป้ายกำกับอยู่ที่ช่องอยู่แล้ว
          */}
          <Title level={isMobile ? 5 : 4} style={{ margin: 0 }} ellipsis={{ rows: 1 }}>
            {isMobile ? "ตั้งค่าข้อมูลผู้ประกอบการ" : "ตั้งค่าข้อมูลผู้ประกอบการของคุณ"}
          </Title>
          {!isMobile && (
            <Text type="secondary" style={{ display: "block", marginTop: 4 }}>
              กรอกชื่อกิจการ โลโก้ และที่อยู่ให้ครบ เพื่อเปิดใช้งานระบบผู้ประกอบการ
              <br />
              ใช้เวลาไม่นาน และแก้ไขภายหลังได้ที่เมนูตั้งค่ากิจการ
            </Text>
          )}
        </div>
        <Button
          type="text"
          size={isMobile ? "small" : "middle"}
          style={{ flex: "none" }}
          onClick={() => dispatch(snoozeFactorySetup())}
        >
          ไว้ทีหลัง
        </Button>
      </div>

      {/* ปุ่มบันทึกเกาะก้นกล่องนี้ (sticky ในฟอร์ม) — เลื่อนอยู่ก็ยังเห็นปุ่มตลอด */}
      <div
        style={{
          maxHeight: isMobile ? "calc(100vh - 68px)" : "72vh",
          overflowY: "auto",
          padding: isMobile ? "16px 16px 8px" : "20px 28px 8px",
        }}
      >
        <SettingFactory embedded />
      </div>
    </Modal>
  );
};

export default FactorySetupGate;
